import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CampaignInsightsService, CampaignOutcome } from './campaign-insights.service';
import { deliveredShare, formatShare } from '../../../services/message/delivery.service';
import { formatCount } from '../../../../utils/sms';
import { isRunning } from '../../../pages/organization/campaigns/campaign-list/campaign-facts';

type Verdict = 'delivered' | 'partial' | 'failed' | 'sending' | 'unconfirmed';

/**
 * Did the campaign get through? The bk-delivery verdict, summed over every
 * send in the campaign: a word first, then a thin bar and the counts. Colour
 * only marks failures.
 */
@Component({
  selector: 'bk-campaign-outcome',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (state) {
      @case ("loading") {
        <span class="d">
          <span class="bk-sr-only">Loading delivery</span>
          <span class="bk-skel d__skel-line"></span>
          <span class="bk-skel d__skel-bar"></span>
        </span>
      }
      @case ("ready") {
        @if (outcome.source === "unsent") {
          <span class="d__none" title="Delivery shows once the first message goes out">Not sent yet</span>
        } @else if (outcome.total === 0) {
          <span class="d__none" title="No delivery receipts have come back for this campaign yet">No receipts yet</span>
        } @else {
          <span class="d" [attr.data-verdict]="verdict" [attr.title]="breakdown">
            <span class="d__verdict" aria-hidden="true">{{ verdictLabel }}</span>
            <span class="bk-sr-only">{{ breakdown }}</span>
            <span class="d__detail" aria-hidden="true">
              <span class="d__bar">
                <span class="d__seg d__seg--ok" [style.flex-grow]="outcome.delivered"></span>
                @if (outcome.failed) {
                  <span class="d__seg d__seg--bad" [style.flex-grow]="outcome.failed"></span>
                }
                <span class="d__seg d__seg--wait" [style.flex-grow]="outcome.inFlight"></span>
              </span>
              <span class="d__count">{{ formatCount(outcome.delivered) }} / {{ formatCount(outcome.total) }}</span>
            </span>
          </span>
        }
      }
      @case ("error") {
        <span class="d__none" title="Delivery receipts could not be loaded">—</span>
      }
      @default {
        <span class="d__none">—</span>
      }
    }
  `,
  styleUrl: './campaign-cells.css',
})
export class CampaignOutcomeComponent implements OnChanges, OnDestroy {
  private insights = inject(CampaignInsightsService);
  private cdr = inject(ChangeDetectorRef);
  private sub?: Subscription;

  /** A campaign row from the list endpoint. */
  @Input({ required: true }) campaign: any;

  state: 'idle' | 'loading' | 'ready' | 'error' = 'idle';
  outcome: CampaignOutcome = { total: 0, delivered: 0, failed: 0, inFlight: 0, source: 'unsent' };

  readonly formatCount = formatCount;

  get verdict(): Verdict {
    const { delivered, failed, inFlight } = this.outcome;
    if (inFlight > 0 && isRunning(this.campaign?.status)) return 'sending';
    if (failed > 0 && delivered === 0) return 'failed';
    if (failed > 0) return 'partial';
    if (inFlight > 0) return 'unconfirmed';
    return 'delivered';
  }

  get verdictLabel(): string {
    const share = formatShare(deliveredShare(this.outcome));
    switch (this.verdict) {
      case 'failed':
        return 'Failed';
      case 'partial':
        return `${formatCount(this.outcome.failed)} failed`;
      case 'sending':
        return `Delivering · ${share}`;
      case 'unconfirmed':
        return `${share} confirmed`;
      default:
        return 'Delivered';
    }
  }

  /** Full breakdown for the tooltip and screen readers. */
  get breakdown(): string {
    const { total, delivered, failed, inFlight } = this.outcome;
    const parts = [`${formatCount(delivered)} delivered`];
    if (failed) parts.push(`${formatCount(failed)} failed`);
    if (inFlight) {
      parts.push(
        isRunning(this.campaign?.status)
          ? `${formatCount(inFlight)} with no receipt yet`
          : `${formatCount(inFlight)} with no receipt`
      );
    }
    return `${parts.join(', ')}, of ${formatCount(total)} SMS sent`;
  }

  ngOnChanges() {
    this.sub?.unsubscribe();
    if (!this.campaign) {
      this.state = 'idle';
      return;
    }

    this.state = 'loading';
    this.sub = this.insights.outcome(this.campaign).subscribe({
      next: (outcome) => {
        this.outcome = outcome;
        this.state = 'ready';
        this.cdr.markForCheck();
      },
      error: () => {
        this.state = 'error';
        this.cdr.markForCheck();
      },
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
}
